import { MOBILE_MAX, TABLET_MAX } from '@property.com.au/ui-kit';
import { createContext, useContext, useEffect, useMemo } from 'react';
import { useState, type FunctionComponent, type PropsWithChildren } from 'react';
import { usePropertyPageQuery } from '../../graphql/usePropertyPageQuery';
import { useUrlContext } from '../../../contexts/UrlContext/UrlContext';

interface AdContext {
  isPageLevelTargetingSet: boolean;
}

export const AdContext = createContext<AdContext | undefined>(undefined);

export const useAdContext = (): AdContext => {
  const context = useContext(AdContext);
  if (context === undefined) {
    throw new Error('AdContext must be used within an AdContext.Provider');
  }
  return context;
};

export const getPlatformValue = (width: number) => {
  if (width <= MOBILE_MAX) {
    return 'msite';
  }
  if (width <= TABLET_MAX) {
    return 'midi';
  }
  return 'web';
};

export const PropertyPageAdContextProvider: FunctionComponent<PropsWithChildren> = ({
  children,
}) => {
  const [isPageLevelTargetingSet, setIsPageLevelTargetingSet] = useState(false);
  const url = useUrlContext();
  const adTargeting = usePropertyPageQuery()?.data?.propertyPageBySlugV3?.adTargeting;

  useEffect(() => {
    if (adTargeting) {
      // Set that google tag is available on the window object level
      window.googletag?.cmd.push(() => {
        window.googletag.pubads().clearTargeting();
        (
          [
            ['platform', getPlatformValue(window.innerWidth)],
            ['proptype', adTargeting.proptype],
            ['state', adTargeting.state],
            ['pcode', adTargeting.pcode],
            ['sub', adTargeting.suburb],
            ['beds', adTargeting.bed],
            ['baths', adTargeting.bath],
            ['cars', adTargeting.cars],
            ['price', adTargeting.price],
            ['pageurl', url.href],
          ] satisfies [string, string][]
        ).forEach(([key, value]) => {
          if (typeof value === 'string' && value.trim() !== '')
            window.googletag.pubads().setTargeting(key, [value]);
        });
        setIsPageLevelTargetingSet(true);
      });
    }

    return () => {
      if (adTargeting) {
        window.googletag?.cmd.push(() => {
          setIsPageLevelTargetingSet(false);
          window.googletag.pubads().clearTargeting();
        });
      }
    };
  }, [adTargeting, url]);

  const contextValue = useMemo(() => ({ isPageLevelTargetingSet }), [isPageLevelTargetingSet]);

  return <AdContext.Provider value={contextValue}>{children}</AdContext.Provider>;
};
